import { Controller } from "@hotwired/stimulus"
import EventHelper from "../helpers/event_helper.js"
import ImageCropper from "../helpers/image_cropper.js"
import RequestHelper from "../helpers/request_helper.js"

const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'svg', 'svg+xml'];
const JQUERY_EVENT_NAMESPACE = 'imageUploads';

export default class extends Controller {
  static get targets() {
    return ['modal', 'image', 'previewImage', 'edit', 'previewVideoModal', 'previewVideo', 'previewGif', 'preview', 'upload', 'remove', 'error', 'replacementNode', 'dropzone'];
  }

  static get values() {
    return {
      width: Number,
      height: Number,
      fillColor: { type: String, default: '#fff' },
      aspectRatio: { type: Number, default: 1 },
      circular: Boolean
    };
  }

  initialize() {
    this.requestHelper = RequestHelper;
  }

  connect() {
    EventHelper.forwardJqueryEvent(this.modalTarget, 'shown.bs.modal', JQUERY_EVENT_NAMESPACE);
    EventHelper.forwardJqueryEvent(this.modalTarget, 'hidden.bs.modal', JQUERY_EVENT_NAMESPACE);
  }

  // Both uploading and deleting replace this controller's own mount with the server's re-rendered
  // uploader before hiding the modal, so the hide-modal action that would otherwise tear the
  // cropper down is already unwired by the time it fires. Cropper keeps a window resize listener
  // alive for as long as it exists, so without this every completed upload leaked one.
  disconnect() {
    EventHelper.stopForwardingJqueryEvents(this.modalTarget, JQUERY_EVENT_NAMESPACE);
    this.destroyCropper();
  }

  // When using Circular cropping we need to enforce PNG
  getUploadType() {
    if (this.circularValue) {
      return "image/png";
    } else {
      return this.uploadType;
    }
  }

  onDragEnter(event) {
    this.suppressDragDefaults(event);
    this.dropzoneTarget.classList.add('highlight');
  }

  onDragLeave(event) {
    this.suppressDragDefaults(event);
    this.dropzoneTarget.classList.remove('highlight');
  }

  onDrop(event) {
    this.suppressDragDefaults(event);
    this.dropzoneTarget.classList.remove('highlight');
    this.loadMediaForCropping(event);
  }

  suppressDragDefaults(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  rotateImage() {
    this.cropper.rotate();
  }

  // The six panels stay on jQuery's own show and hide. jQuery 3 caches each element's pre-hide
  // computed display and restores that value, falling back to the tag default only when a
  // stylesheet still hides the element -- behaviour a native inline-display assignment cannot
  // reproduce for six panels whose cascaded display comes from Bootstrap's modal rules.
  showModal(modal) {
    [this.errorTarget,
      this.editTarget,
      this.previewTarget,
      this.previewVideoModalTarget,
      this.uploadTarget,
      this.removeTarget].every(otherModal => $(otherModal).hide());

    $(this.modalTarget).modal({backdrop: 'static'});
    $(modal).show();
  }

  loadMediaForCropping(event) {
    const files = event.currentTarget.files || event.dataTransfer.files;
    this.dataset = event.currentTarget.dataset;
    this.uploadType = files[0].type;

    (['video/mp4', 'image/gif'].includes(this.uploadType)) ?
      this.loadAnimatedMediaForCropping(event) :
      this.loadImageForCropping(event);
  }

  loadImageForCropping(event) {
    const files = event.currentTarget.files || event.dataTransfer.files;
    this.uploadType = files[0].type;

    let done = url => {
      event.currentTarget.value = '';
      this.imageTarget.src = url;
      this.showModal(this.editTarget);
    }

    let reader, file;

    if (files && files.length > 0) {
      file = files[0];
      if (URL) {
        done(URL.createObjectURL(file));
      } else if (FileReader) {
        reader = new FileReader();
        reader.onload = function (e) {
          done(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  }

  loadAnimatedMediaForCropping(event) {
    this.showModal(this.previewVideoModalTarget);
    if (event.currentTarget.files) {
      this.animatedFile = event.currentTarget.files[0];
    } else {
      this.animatedFile = event.dataTransfer.files[0];
    }
    if (this.uploadType === 'video/mp4') {
      this.previewGifTarget.classList.add('d-none');
      this.previewVideoTarget.classList.remove('d-none');
      this.previewVideoTarget.src = URL.createObjectURL(this.animatedFile);
      this.previewVideoTarget.load();
    } else {
      this.previewGifTarget.classList.remove('d-none');
      this.previewVideoTarget.classList.add('d-none');
      this.previewGifTarget.src = URL.createObjectURL(this.animatedFile);
    }
  }

  previewCroppedImage() {
    this.showModal(this.previewTarget);
    this.previewImageTarget.src = this.getCanvas().toDataURL(this.getUploadType(), 0.9);
  }

  getCanvas() {
    return this.cropper ? this.cropper.canvas() : undefined;
  }

  backToEditImage() {
    this.showModal(this.editTarget);
  }

  persistAnimatedMedia() {
    const rootElement = this.beginUpload();
    const formData = this.uploadFormData();
    formData.append(this.dataset.imageName, this.animatedFile);
    this.submitUpload(formData, rootElement);
  }

  persistCroppedImage() {
    const rootElement = this.beginUpload();
    this.getCanvas().toBlob((blob) => {
      const rawExt = this.getUploadType().split('/')[1] || '';
      const ext = ALLOWED_IMAGE_EXTENSIONS.includes(rawExt) ? rawExt : 'png';
      const formData = this.uploadFormData();
      formData.append(this.dataset.imageName, blob, `upload.${ext}`);
      this.submitUpload(formData, rootElement);
    }, this.getUploadType(), 0.9);
  }

  // Shows the uploading modal and captures the node the server's re-rendered uploader replaces.
  // Both have to happen before any async work starts, so the cropped-image path runs this before
  // handing off to toBlob rather than inside its callback.
  beginUpload() {
    this.showModal(this.uploadTarget);
    return document.querySelector(this.dataset.replacetargetselector);
  }

  uploadFormData() {
    const formData = new FormData();
    formData.append('_method', this.dataset.method);
    return formData;
  }

  submitUpload(formData, rootElement) {
    const context = this;
    const params = Object.assign(RequestHelper.defaultPostOptions(), { "body": formData });
    fetch(this.dataset.path, params).
      then((response) => {
        if (!response.ok) { throw new Error(); }
        return response.text();
      }).
      then(json => JSON.parse(json).html).
      then(html => {
        rootElement.parentNode.replaceWith(context.requestHelper.htmlNodeFromString(html), rootElement);
        rootElement.classList.remove("missing");
        document.getElementById(`replacement-node-${context.dataset.imageName}`).remove();
      }).
      then(() => {
        $(context.modalTarget).modal('hide');
      }).catch(err => context.showModal(context.errorTarget));
  }

  deleteImage(event) {
    event.preventDefault();
    const context = this;
    this.showModal(this.removeTarget);
    const anchor = event.target.closest('a');
    const url = anchor.getAttribute('href');
    const imageName = anchor.dataset.imageName;
    let formData = new FormData();
    formData.append('_method', 'patch');
    if (imageName) {
      formData.append(`${imageName}_delete`, "1");
    }
    fetch(url, Object.assign(RequestHelper.defaultPostOptions(), { "body": formData })).
      then(response => response.text()).
      then(json => JSON.parse(json).html).
      then(html => {
        context.replacementNodeTarget.parentNode.replaceWith(context.requestHelper.htmlNodeFromString(html), context.replacementNodeTarget);
        document.getElementById(`replacement-control-node-${imageName}`).remove();
      }).
      then(() => {
        $(context.modalTarget).modal('hide');
      })
  }

  // Fires on every shown.bs.modal, so tear down any previous instance first rather than orphaning
  // it -- an orphaned Cropper keeps its window resize listener.
  initializeCropper() {
    this.destroyCropper();

    this.cropper = new ImageCropper(this.imageTarget, {
      width: this.widthValue,
      height: this.heightValue,
      aspectRatio: this.aspectRatioValue,
      fillColor: this.fillColorValue,
      circular: this.circularValue
    });
    this.cropper.init();
  }

  destroyCropper() {
    if (!this.cropper) { return; }

    this.cropper.destroy();
    this.cropper = null;
  }
}
